import React, { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { Card, Grid, Loader } from "semantic-ui-react";
import Calendar from "react-calendar";
import BCDashboard from "../ui/BCDashboard";
import BCDashboardList from "../ui/BCDashboardList";
import BCDashboardFeed from "../ui/BCDashboardFeed";
import BCDashboardEvents from "../ui/BCDashboardEvents";
import BCDashboardButtons from "../ui/BCDashboardButtons";
import { BCColorArray } from "../ui/BCColorArray";
import BCPieChart from "../ui/BCPieChart";
import BCLegend from "../ui/BCLegend";
import BCMessage from "../ui/BCMessage";
import { getLODashboard } from "../../api/LOApi";
import { getMockLODashboard } from "../../api/mock/MockLOApi";
import { calculateArrTotal, convertToDollar } from "../../Helpers";
import "../../styles/dashboard.scss";
import "../../styles/calendar.scss";
import "../../styles/main.scss";
import Bar from "../ui/BCBarGraph";
import * as d3 from "d3";
import { FeatureFlags } from "../constants/LocalServerConstants";

const LODashboard = ({ 
  isGreaterThanMobile, 
  isGreaterThanTablet, 
  isSidebar, 
  isMessage, 
  setIsMessage,
  errorResponse, 
  setErrorResponse
}) => {
  const [dashboardData, setDashboardData] = useState(undefined as any);
  const [isLoading, setIsLoading] = useState(true);
  const [calendarDate, setCalendarDate] = useState(new Date());

  const history = useHistory();
  const handleError = useCallback(() => {
    history.push("/error");
  }, [history]);

  const handleNavigateToDeals = useCallback(() => {
    history.push("/deals");
  }, [history]);

  const handleNavigateToActivities = useCallback(() => {
    history.push("/activities");
  }, [history]);

  const convertClosingMonthsToArray = (closingMonths) => {
    return d3.range(dashboardData && dashboardData.closingMonths.length).map((item, index) => ({
      index: index,
      date: closingMonths[index].monthName,
      value: closingMonths[index].closingAmount
    }));
  };

  useEffect(() => {
    const getLODashboardAsync = async () => {
      try {
        const response = FeatureFlags.IS_MOCK ? await getMockLODashboard() : await getLODashboard();
        if (response && response.ok) {
          setIsMessage(false);
          setDashboardData(await response.json());
        }
        else {
          FeatureFlags.IS_DEBUG && console.log(response);
          setErrorResponse(response);
          setIsMessage(true);
        }
      }
      catch (error) {
        FeatureFlags.IS_DEBUG && console.log(error);
        setIsMessage(false);
        handleError();
      }
      setIsLoading(false);
    };

    getLODashboardAsync();
  }, [setIsMessage, handleError]);

  const projectTypeLegendArray = [
    {
      value: "projectTypeName",
      format: "default"
    },
    {
      value: "projectTypeTotal",
      format: "dollar"
    }
  ];

  const LODashboardCharts = ({ ...props }) => {
    return (
      <Grid>
        <Grid.Row>
          <Grid.Column>
            <Card fluid>
              <Card.Header className="dashboard-card-title">Closings by Month</Card.Header>
              <Card.Content className="dashboard-card-content">
                <Card.Description className="dashboard-card-description">
                  {(props.closingMonths) &&
                    <div className="full-width">
                      <Bar
                        data={dashboardData && convertClosingMonthsToArray(dashboardData.closingMonths)}
                        width={700}
                        height={400}
                        top={20}
                        bottom={20}
                        left={100}
                        right={50}
                        graphColors={BCColorArray}
                        yLabel={"Closing Amount (Thousands)"}
                      />
                    </div>
                  }
                </Card.Description>
              </Card.Content>
            </Card>
          </Grid.Column>
        </Grid.Row>
        <Grid.Row className="dashboard-row-layered">
          <Grid.Column>
            <Card fluid>
              <Card.Header className="dashboard-card-title">Portfolio by Product Type</Card.Header>
              <Card.Content className="dashboard-card-content">
                <Card.Description className="dashboard-card-description">
                  {(props.projectTypes) &&
                    <Grid>
                      <Grid.Row>
                        <Grid.Column width={8}>
                          <BCPieChart
                            data={props.projectTypes.filter(x => x.projectTypeTotal !== null)}
                            valueName="projectTypeTotal"
                            addedWidth={0}
                            width={400}
                            height={400}
                            innerRadius={75}
                            outerRadius={200}
                            colorArray={BCColorArray}
                          />
                        </Grid.Column>
                        <Grid.Column width={8}>
                          <div className="vertical-center">
                            <BCLegend
                              data={props.projectTypes}
                              labels={projectTypeLegendArray}
                              colorArray={BCColorArray}
                              shape="circle"
                            />
                          </div>
                        </Grid.Column>
                      </Grid.Row>
                    </Grid>
                  }
                </Card.Description>
              </Card.Content>
            </Card>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    );
  };

  const LoanMetaTotal = ({ ...props }) => {
    if (props.data) {
      return (
        <div className="dashboard-meta-total">
          {convertToDollar(calculateArrTotal(props.data, "amount"))}
        </div>
      );
    }
    else {
      return null;
    }
  };

  const LODashboardLayout = [
    [
      {
        title: "Open Loans",
        size: 5,
        meta: <LoanMetaTotal data={dashboardData && dashboardData.openLoans} />,
        content: <BCDashboardList 
          data={dashboardData && dashboardData.openLoans}
          isGreaterThanTablet={isGreaterThanTablet}
          nameProp={"name"}
          typeProp={"type"}
          valueProp={"amount"}
        />,
        footer: <BCDashboardButtons showInverse inverseText={"NEW PROJECT"} handleSeeAll={handleNavigateToDeals} />
      },
      {
        title: "Closed Loans",
        size: 5,
        meta: <LoanMetaTotal data={dashboardData && dashboardData.closedLoans} />,
        content: <BCDashboardList 
          data={dashboardData && dashboardData.closedLoans}
          isGreaterThanTablet={isGreaterThanTablet}
          nameProp={"name"}
          typeProp={"type"}
          valueProp={"amount"}
        />,
        footer: <BCDashboardButtons handleSeeAll={handleNavigateToDeals} />
      },
      {
        title: "Activities",
        size: 6,
        content: <BCDashboardFeed data={dashboardData && dashboardData.alerts} />,
        footer: <BCDashboardButtons handleSeeAll={handleNavigateToActivities} />
      }
    ],
    [
      {
        custom: <LODashboardCharts 
          closingMonths={dashboardData && dashboardData.closingMonths}
          projectTypes={dashboardData && dashboardData.projectTypes}
        />,
        size: 10
      },
      {
        title: "Upcoming Events",
        size: 6,
        content: <div>
          <div className="dashboard-calendar-container">
            <Calendar
              onChange={setCalendarDate}
              value={calendarDate}
            />
          </div>
          <BCDashboardEvents data={dashboardData && dashboardData.events} />
        </div>
      }
    ]
  ];

  return (
    <div className={(isGreaterThanTablet && isSidebar) ? "hide-sidebar" : "sidebar"}>
      {isLoading ? <Loader className="" active inline="centered" size={"big"} /> : <div>
        <BCMessage
          animation={"fly down"}
          duration={750}
          visible={isMessage}
          transitionOnMount
          styles="full-width"
          onDismiss={() => setIsMessage(false)}
          errorResponse={errorResponse}
        />
        {dashboardData && <BCDashboard
          DashboardLayout={LODashboardLayout}
          isGreaterThanMobile={isGreaterThanMobile}
        />}
      </div>}
    </div>
  );
};

export default LODashboard;